/**
 * BTWOverzicht - BTW Aangifte overzicht
 * EXACT Morris btw.html kopie
 */

import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, BTWOverzicht as BTWOverzichtType, BTWRubriekData, BTWAangifteCreate, BTWSuppletieCreate } from '../../services/boekhouding.service';

const BTWOverzicht = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<BTWOverzichtType | null>(null);

  // Kwartaal uit URL (0 = jaar overzicht)
  const kwartaalFilter = searchParams.get('kwartaal') ? parseInt(searchParams.get('kwartaal')!) : 1;

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  // Ingediende waarden (formulier state)
  const [ingediend, setIngediend] = useState<BTWRubriekData>({
    r1a_omzet: 0, r1a_btw: 0,
    r1b_omzet: 0, r1b_btw: 0,
    r1c_omzet: 0, r1c_btw: 0,
    r1d_omzet: 0, r1d_btw: 0,
    r1e_omzet: 0,
    r2a_omzet: 0, r2a_btw: 0,
    r3a_omzet: 0, r3b_omzet: 0, r3c_omzet: 0,
    r4a_omzet: 0, r4a_btw: 0,
    r4b_omzet: 0, r4b_btw: 0,
    r5b_voorbelasting: 0, r5d_kor: 0,
    betaald: 0,
  });

  // Suppletie formulier
  const [suppletieKwartaal, setSuppletieKwartaal] = useState(1);
  const [suppletieVerschil, setSuppletieVerschil] = useState(0);
  const [suppletieDatum, setSuppletieDatum] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadData();
  }, [huidigJaar, kwartaalFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await boekhoudingService.getBTWOverzicht(huidigJaar, kwartaalFilter);
      setData(response);
      // Zet ingediende waarden in formulier
      setIngediend(response.ingediend);
      // Zet suppletie verschil van eerste kwartaal
      if (response.btw_verschillen.length > 0) {
        setSuppletieVerschil(response.btw_verschillen[0].verschil);
      }
    } catch (err) {
      console.error('Error loading BTW overzicht:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatBedrag = (bedrag: number | undefined): string => {
    const val = Math.round(Number(bedrag) || 0);
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatBedragDecimal = (bedrag: number | undefined): string => {
    const val = Number(bedrag) || 0;
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  // Filter handlers
  const setKwartaal = (q: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('kwartaal', q.toString());
    setSearchParams(params);
  };

  // Bereken 5a totaal (BTW rubrieken 1-4)
  const bereken5a = (d: BTWRubriekData): number => {
    return (Number(d.r1a_btw) || 0) + (Number(d.r1b_btw) || 0) +
           (Number(d.r1c_btw) || 0) + (Number(d.r1d_btw) || 0) +
           (Number(d.r2a_btw) || 0) + (Number(d.r4a_btw) || 0) +
           (Number(d.r4b_btw) || 0);
  };

  // Bereken 5c subtotaal (5a - 5b)
  const bereken5c = (d: BTWRubriekData): number => {
    return bereken5a(d) - (Number(d.r5b_voorbelasting) || 0);
  };

  // Bereken totaal (5c - 5d)
  const berekenTotaal = (d: BTWRubriekData): number => {
    return bereken5c(d) - (Number(d.r5d_kor) || 0);
  };

  // Update ingediend veld
  const updateIngediend = (field: keyof BTWRubriekData, value: string) => {
    setIngediend(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  // Opslaan BTW aangifte
  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (kwartaalFilter === 0) return; // Jaar kan niet opgeslagen worden

    setSaving(true);
    try {
      const saveData: BTWAangifteCreate = {
        jaar: huidigJaar,
        kwartaal: kwartaalFilter,
        ...ingediend
      };
      await boekhoudingService.saveBTWAangifte(saveData);
      await loadData(); // Herlaad data
    } catch (err) {
      console.error('Error saving BTW aangifte:', err);
    } finally {
      setSaving(false);
    }
  };

  // Opslaan suppletie
  const handleSaveSuppletie = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saveData: BTWSuppletieCreate = {
        jaar: huidigJaar,
        kwartaal: suppletieKwartaal,
        verschil: suppletieVerschil,
        ingediend_datum: suppletieDatum
      };
      await boekhoudingService.saveBTWSuppletie(saveData);
      await loadData();
    } catch (err) {
      console.error('Error saving suppletie:', err);
    } finally {
      setSaving(false);
    }
  };

  // Verwijder suppletie
  const handleDeleteSuppletie = async (kwartaal: number) => {
    if (!window.confirm('Suppletie verwijderen?')) return;
    try {
      await boekhoudingService.deleteBTWSuppletie(huidigJaar, kwartaal);
      await loadData();
    } catch (err) {
      console.error('Error deleting suppletie:', err);
    }
  };

  // Update suppletie verschil bij kwartaal wijziging
  const handleSuppletieKwartaalChange = (q: number) => {
    setSuppletieKwartaal(q);
    if (data?.btw_verschillen) {
      const verschil = data.btw_verschillen.find(v => v.kwartaal === q);
      if (verschil) {
        setSuppletieVerschil(verschil.verschil);
      }
    }
  };

  if (loading) {
    return (
      <BoekhoudingLayout title="BTW Aangifte">
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  const werkelijk = data?.werkelijk || {} as BTWRubriekData;
  const isJaar = kwartaalFilter === 0;

  return (
    <BoekhoudingLayout title={`BTW Aangifte ${huidigJaar}`}>
      {/* Filters - Morris style */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>Kwartaal:</label>
          <div className={styles.filterButtons}>
            {[1, 2, 3, 4].map(q => (
              <button
                key={q}
                className={`${styles.filterBtn} ${kwartaalFilter === q ? styles.filterBtnActive : ''}`}
                onClick={() => setKwartaal(q)}
              >
                Q{q}
              </button>
            ))}
            <button
              className={`${styles.filterBtn} ${kwartaalFilter === 0 ? styles.filterBtnActive : ''}`}
              onClick={() => setKwartaal(0)}
            >
              Jaar
            </button>
          </div>
        </div>
      </div>

      {/* BTW Aangifte Tabel - EXACT Morris */}
      <div className={styles.card}>
        <div className={styles.btwAangifteHeader}>
          <h3>{isJaar ? `Jaar ${huidigJaar}` : `Kwartaal ${kwartaalFilter} ${huidigJaar}`}</h3>
        </div>

        <form onSubmit={handleSave}>
          <table className={styles.btwAangifteTable}>
            <thead>
              <tr>
                <th colSpan={2}></th>
                <th colSpan={2} className={styles.headerIngediend}>Ingediend</th>
                <th colSpan={2} className={styles.headerWerkelijk}>Werkelijk</th>
              </tr>
              <tr>
                <th colSpan={2}></th>
                <th className={`${styles.alignRight} ${styles.colOmzet}`}>Omzet</th>
                <th className={`${styles.alignRight} ${styles.colBtw}`}>Btw</th>
                <th className={`${styles.alignRight} ${styles.colOmzet}`}>Omzet</th>
                <th className={`${styles.alignRight} ${styles.colBtw}`}>Btw</th>
              </tr>
            </thead>
            <tbody>
              {/* Rubriek 1: Prestaties binnenland */}
              <tr className={styles.rubriekHeader}>
                <td colSpan={6}><strong>Rubriek 1: Prestaties binnenland</strong></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>1a</td>
                <td>Leveringen/diensten belast met hoog tarief</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1a_omzet}
                    onChange={e => updateIngediend('r1a_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1a_btw}
                    onChange={e => updateIngediend('r1a_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1a_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1a_btw)}</td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>1b</td>
                <td>Leveringen/diensten belast met laag tarief</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1b_omzet}
                    onChange={e => updateIngediend('r1b_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1b_btw}
                    onChange={e => updateIngediend('r1b_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1b_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1b_btw)}</td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>1c</td>
                <td>Leveringen belast met overige tarieven, behalve 0%</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1c_omzet}
                    onChange={e => updateIngediend('r1c_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1c_btw}
                    onChange={e => updateIngediend('r1c_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1c_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1c_btw)}</td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>1d</td>
                <td>Privégebruik auto</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1d_omzet}
                    onChange={e => updateIngediend('r1d_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1d_btw}
                    onChange={e => updateIngediend('r1d_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1d_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1d_btw)}</td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>1e</td>
                <td>Leveringen/diensten belast met 0% of niet bij u belast</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r1e_omzet}
                    onChange={e => updateIngediend('r1e_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r1e_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
              </tr>

              {/* Rubriek 2: Verleggingsregeling */}
              <tr className={styles.rubriekHeader}>
                <td colSpan={6}><strong>Rubriek 2: Verleggingsregeling Binnenland</strong></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>2a</td>
                <td>Leveringen/diensten omzetbelasting naar u is verlegd</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r2a_omzet}
                    onChange={e => updateIngediend('r2a_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r2a_btw}
                    onChange={e => updateIngediend('r2a_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r2a_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r2a_btw)}</td>
              </tr>

              {/* Rubriek 3: Prestaties buitenland */}
              <tr className={styles.rubriekHeader}>
                <td colSpan={6}><strong>Rubriek 3: Prestaties naar of in het buitenland</strong></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>3a</td>
                <td>Leveringen naar landen buiten de EU (uitvoer)</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r3a_omzet}
                    onChange={e => updateIngediend('r3a_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r3a_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>3b</td>
                <td>Leveringen naar of diensten in landen binnen de EU</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r3b_omzet}
                    onChange={e => updateIngediend('r3b_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r3b_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>3c</td>
                <td>Installatie/afstandsverkopen binnen de EU</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r3c_omzet}
                    onChange={e => updateIngediend('r3c_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r3c_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
              </tr>

              {/* Rubriek 4: Prestaties vanaf buitenland */}
              <tr className={styles.rubriekHeader}>
                <td colSpan={6}><strong>Rubriek 4: Prestaties vanaf het buitenland</strong></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>4a</td>
                <td>Leveringen/diensten uit landen buiten de EU</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r4a_omzet}
                    onChange={e => updateIngediend('r4a_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r4a_btw}
                    onChange={e => updateIngediend('r4a_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r4a_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r4a_btw)}</td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>4b</td>
                <td>Leveringen/diensten uit landen binnen de EU</td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r4b_omzet}
                    onChange={e => updateIngediend('r4b_omzet', e.target.value)} disabled={isJaar} />
                </td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r4b_btw}
                    onChange={e => updateIngediend('r4b_btw', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r4b_omzet)}</td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r4b_btw)}</td>
              </tr>

              {/* Rubriek 5: Voorbelasting */}
              <tr className={styles.rubriekHeader}>
                <td colSpan={6}><strong>Rubriek 5: Voorbelasting en eindtotaal</strong></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>5a</td>
                <td>Omzetbelasting (rubrieken 1 t/m 4)</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.berekend}`}>{formatBedrag(bereken5a(ingediend))}</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(bereken5a(werkelijk))}</td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>5b</td>
                <td>Voorbelasting</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r5b_voorbelasting}
                    onChange={e => updateIngediend('r5b_voorbelasting', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r5b_voorbelasting)}</td>
              </tr>
              <tr className={styles.subtotaalRow}>
                <td className={styles.rubriekNr}>5c</td>
                <td><strong>Subtotaal (rubriek 5a min 5b)</strong></td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.berekend}`}><strong>{formatBedrag(bereken5c(ingediend))}</strong></td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}><strong>{formatBedrag(bereken5c(werkelijk))}</strong></td>
              </tr>
              <tr>
                <td className={styles.rubriekNr}>5d</td>
                <td>Vermindering volgens de kleineondernemersregeling</td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.r5d_kor}
                    onChange={e => updateIngediend('r5d_kor', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.r5d_kor)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className={styles.totaalRow}>
                <td colSpan={2}><strong>Totaal</strong></td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.berekend}`}><strong>{formatBedrag(berekenTotaal(ingediend))}</strong></td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}><strong>{formatBedrag(berekenTotaal(werkelijk))}</strong></td>
              </tr>
              <tr className={styles.betaaldRow}>
                <td colSpan={2}><strong>Betaald</strong></td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={styles.alignRight}>
                  <input type="number" className={styles.inputBedrag} value={ingediend.betaald}
                    onChange={e => updateIngediend('betaald', e.target.value)} disabled={isJaar} />
                </td>
                <td className={`${styles.alignRight} ${styles.emptyCell}`}></td>
                <td className={`${styles.alignRight} ${styles.werkelijk}`}>{formatBedrag(werkelijk.betaald)}</td>
              </tr>
            </tfoot>
          </table>

          {!isJaar && (
            <div className={styles.formActions}>
              <button type="submit" className={styles.btnPrimary} disabled={saving}>
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* BTW Suppleties - EXACT Morris */}
      <div className={styles.card} style={{ marginTop: '1.5rem' }}>
        <h3>BTW Suppleties {huidigJaar}</h3>

        {data?.suppleties.suppleties && data.suppleties.suppleties.length > 0 ? (
          <table className={styles.btwAangifteTable} style={{ marginBottom: '1.5rem' }}>
            <thead>
              <tr>
                <th>Kwartaal</th>
                <th className={styles.alignRight}>Verschil</th>
                <th>Ingediend op</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.suppleties.suppleties.map(s => (
                <tr key={s.kwartaal}>
                  <td>Q{s.kwartaal}</td>
                  <td className={styles.alignRight}>{formatBedragDecimal(s.verschil)}</td>
                  <td>{s.datum}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnDanger}
                      onClick={() => handleDeleteSuppletie(s.kwartaal)}
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={styles.totaalRow}>
                <td><strong>Totaal</strong></td>
                <td className={styles.alignRight}><strong>{formatBedragDecimal(data.suppleties.totaal)}</strong></td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <p style={{ color: 'var(--gray-500)', marginBottom: '1.5rem' }}>
            Geen suppleties ingediend voor {huidigJaar}.
          </p>
        )}

        <h4>Nieuwe suppletie toevoegen</h4>

        {/* Verschillen tabel */}
        <table className={styles.btwAangifteTable} style={{ marginBottom: '1rem', maxWidth: '500px' }}>
          <thead>
            <tr>
              <th>Kwartaal</th>
              <th className={styles.alignRight}>Ingediend</th>
              <th className={styles.alignRight}>Werkelijk</th>
              <th className={styles.alignRight}>Verschil</th>
            </tr>
          </thead>
          <tbody>
            {data?.btw_verschillen.map(v => (
              <tr key={v.kwartaal}>
                <td>Q{v.kwartaal}</td>
                <td className={styles.alignRight}>{formatBedragDecimal(v.ingediend)}</td>
                <td className={styles.alignRight}>{formatBedragDecimal(v.werkelijk)}</td>
                <td className={`${styles.alignRight} ${v.verschil > 0 ? styles.textDanger : v.verschil < 0 ? styles.textSuccess : ''}`}>
                  <strong>{formatBedragDecimal(v.verschil)}</strong>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={styles.totaalRow}>
              <td colSpan={3}><strong>Totaal</strong></td>
              <td className={styles.alignRight}>
                <strong>{formatBedragDecimal(data?.btw_verschillen.reduce((sum, v) => sum + Number(v.verschil), 0))}</strong>
              </td>
            </tr>
          </tfoot>
        </table>

        <form onSubmit={handleSaveSuppletie} className={styles.suppletieForm}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Kwartaal</label>
              <select
                value={suppletieKwartaal}
                onChange={e => handleSuppletieKwartaalChange(parseInt(e.target.value))}
              >
                {data?.btw_verschillen.map(v => (
                  <option key={v.kwartaal} value={v.kwartaal}>
                    Q{v.kwartaal} ({formatBedragDecimal(v.verschil)})
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Verschil (&#8364;)</label>
              <input
                type="number"
                step="0.01"
                value={suppletieVerschil}
                onChange={e => setSuppletieVerschil(parseFloat(e.target.value) || 0)}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Ingediend op</label>
              <input
                type="date"
                value={suppletieDatum}
                onChange={e => setSuppletieDatum(e.target.value)}
                required
              />
            </div>
            <div className={styles.formGroup} style={{ alignSelf: 'flex-end' }}>
              <button type="submit" className={styles.btnPrimary} disabled={saving}>
                Toevoegen
              </button>
            </div>
          </div>
        </form>
      </div>
    </BoekhoudingLayout>
  );
};

export default BTWOverzicht;
